import formatIntegerBySpace from '../../../../helpers/formatters/format-integer-by-space'
import { getShipmentDisplayCalculated } from '../../../../helpers/entities/shipment/get-shipment-display-calculated'
import Shipment from '../../../../types/api/shipment'
import UploadedFile from '../../../../types/api/uploaded-file'

export type MetricTone = 'success' | 'danger'

export interface ManagerShipmentRow {
    key: string
    documentId: string
    id: number
    clientName: string
    shipmentId: number | string
    createdAt: string
    percent: number
    commission: number
    manager?: string
    blankPreviewFiles?: UploadedFile[]
}

interface ManagerMonthlyStatsRaw {
    commission: number | null
    shipmentCount: number
}

export interface ManagerMonthlyStatsFormatted {
    commission: string | null
    commissionTone: MetricTone | null
    shipmentCount: string | null
}

export function getCuratorPercent(
    shipment: Shipment,
    userDocumentId: string,
    clientPercentByDocId?: Map<string, number>
): number | null {
    const clientDocId = shipment.client?.documentId

    if (clientDocId && clientPercentByDocId?.has(clientDocId)) {
        return clientPercentByDocId.get(clientDocId) ?? null
    }

    const curator = shipment.client?.curators?.find(
        item => item.user?.documentId === userDocumentId
    )

    if (curator?.percent == null) {
        return null
    }

    return curator.percent
}

export function calcShipmentCommission(profit: number, percent: number): number {
    return (profit * percent) / 100
}

function roundMoney(value: number): number {
    return Math.round(value)
}

function formatMoney(value: number): string {
    return formatIntegerBySpace(roundMoney(value))
}

function toneFromSignedValue(value: number): MetricTone | null {
    if (value === 0) return null

    return value > 0 ? 'success' : 'danger'
}

export function mapShipmentToRow(
    shipment: Shipment,
    userDocumentId: string,
    clientPercentByDocId?: Map<string, number>
): ManagerShipmentRow | null {
    const percent = getCuratorPercent(shipment, userDocumentId, clientPercentByDocId)

    if (percent == null) {
        return null
    }

    const profit = getShipmentDisplayCalculated(shipment)?.profitOfAllCopies ?? 0

    return {
        key: shipment.documentId,
        documentId: shipment.documentId,
        id: shipment.id,
        clientName: shipment.client?.name ?? '—',
        shipmentId: shipment.id ?? '—',
        createdAt: shipment.createdAt,
        percent,
        commission: calcShipmentCommission(profit, percent),
        manager: shipment.user_audit?.created_by_permissions_user?.username,
        blankPreviewFiles: shipment.blankPreviewFiles,
    }
}

function calcManagerMonthlyStatsRaw(rows: ManagerShipmentRow[]): ManagerMonthlyStatsRaw {
    if (!rows.length) {
        return {
            commission: null,
            shipmentCount: 0,
        }
    }

    const commission = rows.reduce((sum, row) => sum + row.commission, 0)

    return {
        commission,
        shipmentCount: rows.length,
    }
}

function formatManagerMonthlyStats(raw: ManagerMonthlyStatsRaw): ManagerMonthlyStatsFormatted {
    return {
        commission: raw.commission == null ? null : formatMoney(raw.commission),
        commissionTone: raw.commission == null ? null : toneFromSignedValue(raw.commission),
        shipmentCount: raw.shipmentCount === 0 ? null : String(raw.shipmentCount),
    }
}

export function calcManagerMonthlyStats(rows: ManagerShipmentRow[]): ManagerMonthlyStatsFormatted {
    return formatManagerMonthlyStats(calcManagerMonthlyStatsRaw(rows))
}
